import type { ColumnDef } from '@tanstack/react-table';
import { Loader2Icon, MinusIcon, PlusIcon } from 'lucide-react';

import { ActionButton } from '@/_containers/actions';

import { ColumnHeader } from '../column-header';
// import { CellName } from '../typography';

// Compute vertical segment classes for child rows once, outside render
function getChildVerticalBounds({
  isFirstChild,
  isLastChild,
  isOnlyChild,
}: {
  isFirstChild: boolean;
  isLastChild: boolean;
  isOnlyChild: boolean;
}) {
  // First child: full height to connect above the parent and through the child
  if (isFirstChild && !isOnlyChild) return 'top-[-1px] bottom-[-1px]';

  // Middle children: full height
  if (!isFirstChild && !isLastChild) return 'top-[-1px] bottom-[-1px]';

  // Last child and only child: cap at midline
  return 'top-[-1px] bottom-1/2';
}

type GroupButtonColDefOptions = {
  isLoading?: (rowId: string) => boolean;
};

export function groupButtonColDef<T>(options?: GroupButtonColDefOptions) {
  const columns: ColumnDef<T>[] = [
    {
      accessorKey: 'expand',
      // Make the td relative and remove padding so connectors can span full height without gaps
      meta: { name: '', className: 'relative w-8 max-w-8 p-0' },
      header: ({ column }) => <ColumnHeader align="center" column={column} />,
      cell: ({ row }) => {
        const isExpanded = row.getIsExpanded();
        const canExpand = row.getCanExpand();
        const isCurrentlyLoading = canExpand && (options?.isLoading?.(row.id) ?? false);

        // Determine if this row is a child and its index within siblings to draw connectors
        const parent = (row.getParentRow ? row.getParentRow() : undefined) as typeof row | undefined;
        const isChild = !!parent;
        const siblings = (parent?.subRows as (typeof row)[] | undefined) ?? [];
        const index = isChild ? siblings.findIndex((r) => r.id === row.id) : -1;
        const isFirstChild = isChild && index === 0;
        const isLastChild = isChild && index === siblings.length - 1;
        const isOnlyChild = isChild && siblings.length === 1;

        return (
          <div className="flex h-full items-center justify-center">
            {canExpand ? (
              // Parent row: show button and, if expanded, a connector downwards
              <>
                {isExpanded && (
                  <span className="bg-primary pointer-events-none absolute top-1/2 -bottom-px left-1/2 w-px -translate-x-1/2" />
                )}
                <ActionButton
                  icon={isCurrentlyLoading ? Loader2Icon : isExpanded ? MinusIcon : PlusIcon}
                  title="Expand"
                  size="xs"
                  onClick={row.getToggleExpandedHandler()}
                  className="hover:text-accent group z-1 text-white opacity-90 hover:opacity-100"
                  iconClassName={isCurrentlyLoading ? 'animate-spin text-white' : 'group-hover:text-accent text-white'}
                  as="contained"
                />
              </>
            ) : isChild ? (
              // Child row: draw connectors only if it actually has a parent
              <>
                {/* vertical segment */}
                <span
                  className={
                    'bg-primary pointer-events-none absolute left-1/2 w-px -translate-x-1/2 ' +
                    getChildVerticalBounds({ isFirstChild: !!isFirstChild, isLastChild: !!isLastChild, isOnlyChild })
                  }
                />
                {/* horizontal stub */}
                <span className="bg-primary pointer-events-none absolute top-1/2 left-1/2 h-px w-3 -translate-y-1/2" />
                {/* keep spacing aligned with icon column width */}
                <div className="size-5" />
              </>
            ) : (
              // Top-level leaf row: no connectors
              <div className="size-5" />
            )}
          </div>
        );
      },
      enableSorting: false,
      enableResizing: false,
    },
  ];

  return columns;
}
